<template>
  <div class="bm-card" :class="railClass" :data-testid="`batch-${batch.id}`">
    <div
      class="bm-card__header"
      :data-testid="`batch-row-${batch.id}`"
      role="button"
      tabindex="0"
      :aria-expanded="String(expanded)"
      @click="$emit('toggle', batch.id)"
      @keydown.enter.prevent="$emit('toggle', batch.id)"
      @keydown.space.prevent="$emit('toggle', batch.id)"
    >
      <div class="bm-card__ident">
        <div class="bm-card__idline">
          <span class="bm-card__id">{{ batch.id }}</span>
          <!-- #475: the distributor's name. Plain text; never part of the id. -->
          <span
            v-if="batch.displayName"
            class="bm-card__name"
            :data-testid="`batch-name-${batch.id}`"
          >{{ batch.displayName }}</span>
          <BatchStatusChip
            :status="batch.status"
            gloss
            :money-taken="blockedMoneyTaken"
            :partial="blockedPartial"
            :testid="`chip-${batch.id}`"
          />
          <!-- The released batch keeps a permanent held-back record. -->
          <span
            v-if="batch.heldBackCount > 0"
            class="bm-card__heldpill"
            :data-testid="`held-back-pill-${batch.id}`"
          >{{ batch.heldBackCount }} held back</span>
        </div>
        <div class="bm-card__sub">
          {{ batch.orders.length }} {{ batch.orders.length === 1 ? 'order' : 'orders' }} &middot;
          {{ money(total) }}<template v-if="batch.opened"> &middot;
            <span :data-testid="`batch-opened-${batch.id}`">opened {{ batch.opened }}</span></template>
        </div>
      </div>

      <div class="bm-card__trigger" :data-testid="`trigger-${batch.id}`">
        <!--
          A batch whose last release attempt FAILED leads with that, ahead of
          its state (#2772). It is reachable from HOLDING now, so it must be
          tested BEFORE the holding branch or a failed batch renders the
          ordinary "your review, then release" trigger and the error is
          invisible until the row is expanded.

          Gated explicitly rather than left as the trailing `v-else` it used
          to be: a bare `v-else` also swallows a legacy row whose state is
          NULL and labels it a send failure.
        -->
        <!--
          BLOCKED LEADS, ahead of the generic failure banner below.

          A blocked batch also carries `last_release_error`, so it would
          otherwise render "Send failed -- check each order before retrying",
          which is true but understates it: that copy says nothing about money
          having been taken, and a reader who has seen it before will treat
          this like any other retryable send failure.

          This is the one batch state where a card has been charged for goods
          that are NOT in production and nothing automatic will resolve it. It
          says so plainly. The per-order detail, amounts and the retry/abandon
          controls are unit M; this is the honest minimum until then.
        -->
        <div
          v-if="isReleaseBlocked"
          class="bm-trigger__failed"
          :data-testid="`release-blocked-${batch.id}`"
        >
          <i class="material-icons bm-trigger__icon" aria-hidden="true">report_problem</i>
          <!--
            WHAT THE BANNER MAY SAY ABOUT MONEY depends on the stranded items
            (#424: it said "charged but did not reach the decorator" above a
            panel saying nobody was charged, for an uncharged decorator
            refusal). Three cases, never a guess: a charged stranded order,
            stranded orders all known uncharged, and not-loaded-yet -- which
            neither claims nor denies a charge.
          -->
          <!--
            PARTIAL (#424 retest): at least one order WAS sent, so "the batch
            was not sent" and any batch-wide money claim are false. Counts
            only; money is stated about the refused orders alone.
          -->
          <span v-if="blockedPartial" data-testid="blocked-copy-partial">
            <b>Release blocked.</b> {{ blockedPartial.notSent }} of
            {{ blockedPartial.total }} orders were not sent to the decorator;
            the rest were sent.
            {{ blockedPartial.chargedSentence }}
            <template v-if="blockedMoneyTaken === true">
              Some of the orders that were not sent were <b>charged</b> but did
              <b>not</b> reach the decorator &mdash; retry to send them, or
              contact support to sort out the payments.
            </template>
            <template v-else-if="blockedMoneyTaken === false">
              <b>No money was taken</b> for the {{ strandedItems.length }}
              refused {{ strandedItems.length === 1 ? 'order' : 'orders' }}.
              The reason is listed under each one below.
            </template>
          </span>
          <span v-else-if="blockedMoneyTaken === true" data-testid="blocked-copy-charged">
            <b>Release blocked.</b> Some orders in this batch were
            <b>charged</b> but did <b>not</b> reach the decorator, so the batch
            was not sent. Nothing was closed at the decorator &mdash; retry to
            send them, or contact support to sort out the payments.
          </span>
          <span v-else-if="blockedMoneyTaken === false" data-testid="blocked-copy-uncharged">
            <b>Release blocked.</b> Some orders in this batch could not be sent
            to the decorator, so the batch was not sent. <b>No money was taken</b>
            for those orders. The reason is listed under each one below.
          </span>
          <span v-else data-testid="blocked-copy-unknown">
            <b>Release blocked.</b> Some orders in this batch did not reach the
            decorator, so the batch was not sent. Check below whether any of
            them were charged.
          </span>
        </div>

        <div v-else-if="hasFailedAttempt" class="bm-trigger__failed">
          <i class="material-icons bm-trigger__icon" aria-hidden="true">error_outline</i>
          <!--
            THE CHARGE CLAIM WAS REMOVED HERE, not reworded (#3184).
            
            This said "no cards charged" unconditionally, which was true while
            release ran with no `charge_fn`. It is no longer safe to say.
            `batch_release.py` CHARGES at step 1 (:497) and only then submits
            and closes, while `_record_release_failure` is called at :594
            (close failed) and :632 (state changed mid-release) -- both AFTER
            the charge. A failed batch may therefore have charged cards.

            It does not claim the opposite either, because this surface cannot
            know: `chargedTotal` is a client-side sum over non-held orders, not
            the backend's `charged_total_cents`, so "cards were charged" would
            be the same guess with the sign flipped. The banner reports the
            failure and sends the distributor to the orders for the truth.
          -->
          <span>Send failed {{ batch.attempted }} &mdash; check each order below before retrying. {{ batch.failure }}</span>
        </div>

        <!-- Open carries its TRIGGER, not just its state: both conditions. -->
        <template v-else-if="batch.status === 'open'">
          <!--
            The clock line only when there IS a clock. `dueLabel` is "" when
            the time rule is off or a permanent store's batch has no first
            order yet, and printing the line anyway read "Releases  ·  left".
            With BOTH closing rules off the batch never closes on its own;
            that is said in the rules panel's own words.
          -->
          <div
            v-if="batch.trigger.dueLabel"
            class="bm-trigger__clock"
            :data-testid="`trigger-clock-${batch.id}`"
          >
            <i class="material-icons bm-trigger__icon" aria-hidden="true">schedule</i>
            <span>Releases <b>{{ batch.trigger.dueLabel }}</b></span>
            <span class="bm-trigger__quiet">&middot; {{ batch.trigger.remaining }} left</span>
            <span class="bm-trigger__quiet">
              &middot; {{ rules.releaseMode === 'automatic' ? 'automatically' : 'then waits for you' }}
            </span>
          </div>
          <div
            v-else-if="!rules.timeRuleEnabled && !rules.dollarRuleEnabled"
            class="bm-trigger__clock"
          >
            <i class="material-icons bm-trigger__icon" aria-hidden="true">schedule</i>
            <span>Never closes automatically</span>
          </div>
          <!-- "releases early" is false when the blanks rule is off. -->
          <div
            v-if="rules.dollarRuleEnabled"
            class="bm-threshold"
            :data-testid="`threshold-${batch.id}`"
          >
            <div class="bm-threshold__labels">
              <span>{{ money(batch.blanks) }} of blanks</span>
              <span class="bm-threshold__target">{{ money(rules.threshold) }} releases early</span>
            </div>
            <div class="bm-threshold__track">
              <div
                class="bm-threshold__fill"
                :class="{ 'bm-threshold__fill--met': thresholdPct >= 100 }"
                :style="{ width: thresholdPct + '%' }"
              />
            </div>
          </div>
        </template>

        <!-- Holding: amber, and a task rather than an error. No error icon. -->
        <div v-else-if="batch.status === 'holding'" class="bm-trigger__holding">
          <i class="material-icons bm-trigger__icon" aria-hidden="true">how_to_reg</i>
          <span>
            Closed to new orders {{ batch.holdingSince }} ago &mdash; <b>your review, then release</b>
          </span>
        </div>

        <div v-else-if="batch.status === 'released'" class="bm-trigger__released">
          <i class="material-icons bm-trigger__icon" aria-hidden="true">lock</i>
          <!--
            WHEN it closed and WHEN it was released. The status chip already
            says "Sent to the decorator", so the destination is named only
            when the payload knows it -- a bare "sent to" repeated the chip
            and ended in nothing.
          -->
          <span :data-testid="`released-line-${batch.id}`">{{ releasedLine }}</span>
        </div>

        <!--
          RELEASE PROGRESS, on the main card so it is visible collapsed or
          expanded (Jake, 2026-10-13: in the expanded footer it sat below a
          682-order list and the feature looked missing). Independent of the
          trigger chain above: it keys on `releaseInFlight`, SERVER state.

          Present only while the drain is running, because on a holding batch
          there is nothing to report and an empty bar would read as "stuck at
          zero" rather than "not started".

          The NUMBERS are rendered beside the bar, not only encoded in its
          width: "3 of 9" is what an operator reads out to somebody else,
          and a bare bar cannot be read out at all.
        -->
        <!--
          A RELEASING BATCH WHOSE BACK-OFFICE CLOSE FAILED (#4665; Jake,
          2026-10-22). Every order reached the decorator; the last step, the
          close that releases the batch into production, keeps being refused.
          Said in plain words on the card, collapsed or expanded, INSTEAD of
          the progress bar: that bar sits at 100% reading "Finishing up"
          forever, which looks like a hang rather than a stop. The actions
          (Retry release, Give up...) are in the expanded detail.

          Gated on the SERVED `closeRetry`, never on `failure`: a releasing
          batch can carry an older episode's token while a new drain runs
          normally, and lighting this there is a false alarm.
        -->
        <div
          v-if="closeFailed"
          class="bm-trigger__failed"
          :data-testid="`close-failed-${batch.id}`"
        >
          <i class="material-icons bm-trigger__icon" aria-hidden="true">sync_problem</i>
          <span v-if="closeRetryStopped">
            <b>Not released &mdash; automatic retries have stopped.</b> The
            orders that were paid for reached the decorator, but the decorator's
            system did not accept the last step that releases the batch into
            production, so production has not started. Nothing changes until
            you press <b>Retry release</b>. Or give up and close the batch; any
            order that took money without reaching the decorator is then yours
            to refund by hand.
          </span>
          <span v-else>
            <b>Not released yet.</b> The orders that were paid for reached the
            decorator, but the decorator's system has not accepted the last step
            that releases the batch into production. The system is still
            retrying automatically, less often as time goes on;
            <b>Retry release</b> tries again now. If you give up and close the
            batch, any order that took money without reaching the decorator is
            yours to refund by hand.
          </span>
        </div>

        <div
          v-if="releaseInFlight && !closeFailed"
          class="bm-release-progress"
          :data-testid="`release-progress-${batch.id}`"
        >
          <div
            class="bm-release-progress__rail"
            role="progressbar"
            :aria-valuenow="progress ? progress.resolved : 0"
            :aria-valuemin="0"
            :aria-valuemax="progress ? progress.itemCount : 0"
          >
            <div
              class="bm-release-progress__fill"
              :style="{ width: (releasePct * 100).toFixed(1) + '%' }"
            />
          </div>
          <p class="bm-release-progress__label">
            <template v-if="progress && progress.itemCount">
              Sent <b>{{ progress.done }}</b> of <b>{{ progress.itemCount }}</b> orders
              to {{ batch.decoratorTarget }}.
              <!--
                Held-back items are named separately rather than folded into
                the total. They are the reason `done` can stop short of the
                count, and an operator watching a bar halt needs to know it
                halted for a REASON that is already recorded.
              -->
              <template v-if="progress.counts && progress.counts.held_back">
                {{ progress.counts.held_back }} held back.
              </template>
            </template>
            <template v-else>
              Starting the release&hellip;
            </template>
            <span
              v-if="etaInfo.label"
              class="bm-release-progress__eta"
              :data-testid="`release-eta-${batch.id}`"
            >{{ etaInfo.label }}</span>
          </p>
        </div>


      </div>

      <div class="bm-card__chev">
        <i class="material-icons" aria-hidden="true">{{ expanded ? 'expand_less' : 'expand_more' }}</i>
      </div>
    </div>

    <div v-if="expanded" class="bm-detail" :data-testid="`batch-detail-${batch.id}`">
      <div class="bm-detail__top">
        <div class="bm-stats">
          <div class="bm-stat">
            <div class="bm-stat__label">Orders</div>
            <div class="bm-stat__value">{{ batch.orders.length }}</div>
            <div v-if="heldOrders.length" class="bm-stat__sub">{{ heldOrders.length }} held back</div>
          </div>
          <div class="bm-stat">
            <!--
              THE ORDER VALUE (goods less discounts), NOT THE MONEY COLLECTED
              (B-22, 2026-10-20). Cards are charged the order total, which also
              carries tax and shipping, so this figure is always at or below
              the charge ledger. It was labelled "Batch value" / "Sent value",
              which read as the amount charged and disagreed with the stranded
              header's "collected" figure. Money actually collected is shown
              where it matters, from the ledger, on the stranded-orders list.
            -->
            <div class="bm-stat__label">{{ batch.status === 'released' ? 'Order value sent' : 'Order value' }}</div>
            <div
              class="bm-stat__value"
              :data-testid="`batch-value-${batch.id}`"
            >{{ money(batch.status === 'released' ? charged : total) }}</div>
            <div class="bm-stat__sub">before tax and shipping</div>
            <div v-if="batch.status === 'released' && heldOrders.length" class="bm-stat__sub">
              {{ money(total - charged) }} held back
            </div>
          </div>
          <div class="bm-stat">
            <div class="bm-stat__label">Blank goods</div>
            <div class="bm-stat__value">{{ money(batch.blanks) }}</div>
            <div class="bm-stat__sub">
              {{ batch.blanks >= rules.threshold
                ? 'Free freight'
                : money(rules.threshold - batch.blanks) + ' to free freight' }}
            </div>
          </div>
          <div v-if="decoratorText" class="bm-stat">
            <div class="bm-stat__label">Decorator</div>
            <div
              class="bm-stat__value"
              :data-testid="`batch-decorator-${batch.id}`"
            >{{ decoratorText }}</div>
            <div class="bm-stat__sub">
              {{ batch.status === 'released' ? 'In production' : 'Receives this batch' }}
            </div>
          </div>
        </div>

        <!--
          #475. Name and description, and what the back office holds. Shown on
          every state; editable only while a back-office close is ahead.
        -->
        <BatchDetails
          :batch="batch"
          @updated="(updated) => $emit('details-updated', updated)"
        />

        <!--
          When money moves, batch level. Open and Holding only -- a released
          batch has already been charged, so the forward statement would be
          wrong there (#3184).
        -->
        <ChargeAtReleaseNote
          v-if="editable"
          testid="charge-at-release-block"
          :extra="batch.status === 'holding'
            ? 'Fix anything wrong now -- this is the review window.'
            : 'Orders can still arrive and still be edited.'"
        />

        <!--
          A failed attempt leads with the money question, because release is
          one-way with no retract and the first thing a distributor needs to
          know about an error is whether money moved.

          It used to lead with the ANSWER ("no cards charged"), which was true
          only while `charge_fn` defaulted to `_charge_nothing` (#2790).
          Charging is wired now, and `batch_release.py` charges at step 1
          (:497) BEFORE the submit and close whose failures are recorded at
          :594 and :632 -- so a failed batch may well have charged cards
          (#3184). This surface cannot tell which, so it leads with the
          question and says where the answer is instead of guessing.
        -->
        <!--
          BACK-OFFICE REGISTRATION FAILED, PERMANENTLY.

          A different failure from `bm-failed` above, and rendered
          separately rather than folded into it. That one means "we tried to
          send this batch and could not"; this means "this batch was never
          registered with the back office", so it cannot be sent at all --
          release refuses without a back-office id.

          THIS BLOCK IS WHY THE BACKEND MAY STOP RETRYING. Opening a batch
          in tf-deconetwork used to retry forever, and every attempt created
          a real record on a partner's production system (~40,000 of them
          before it was caught). The retry now stops after six attempts. A
          bound that stops is only safe if a human can SEE the stopped
          batch, which is this banner, and RESTART it, which is the button.
          Delete this and the hard stop becomes a silent strand.

          Outside the v-if/v-else-if footer chain deliberately: a batch can
          have BOTH failures at once, and the chain would show only whichever
          arm came first.
        -->
        <!--
          THE STRANDED-ORDERS SURFACE (unit M). Jake, 2026-09-15: *"make sure
          the UI reflects the issue. A human will need to be involved here."*

          Gated on the STATE, not on the presence of stranded items and not on
          held-back orders. Three reasons, and the third is the dangerous one:
          the state is the one authoritative fact the server writes in the
          same statement that refuses the close; an item list can be empty
          while the batch is genuinely blocked (the close gate reads the
          ledger, which sees charges the item rows do not); and `held_back`
          means NO money moved at charge time, so keying on it would tell a
          distributor their shopper was charged when nothing was taken.
        -->
        <!--
          AND ON A RELEASED BATCH THAT STILL OWES REFUNDS (option A, Jake
          2026-09-28). Abandon closes the batch and stamps it released; the
          orders whose shoppers are owed a refund must stay in view until
          each is refunded, or the confirm dialog's "We will list them here"
          is a lie. `refundsOwed` is served (derived from recorded refunds),
          so this reads the server's word, not a client guess -- and a batch
          released before abandon could flag anything owes nothing and stays
          inert. Only the owed orders are listed, and no batch action: the
          batch is closed.
        -->
        <StrandedOrders
          v-if="isReleaseBlocked || owesRefunds"
          :batch-id="batch.id"
          :items="owesRefunds ? owedItems : strandedItems"
          :busy-item-ids="busyItemIds"
          :refunds-only="owesRefunds"
          @retry-item="$emit('retry-item', $event)"
          @rearm-item="$emit('rearm-item', $event)"
        />

        <!--
          Offered on a RELEASING batch whose close failed too (Jake,
          2026-10-22). Same component, same confirmation, same endpoint; the
          confirmation's body is worded for that case below, because the
          blocked wording ("No money was taken for the orders still
          outstanding") would describe orders that do not exist there.
        -->
        <template v-if="isReleaseBlocked || closeFailed">

          <!--
            ABANDON IS DESTRUCTIVE AND IRREVERSIBLE FROM HERE. It closes the
            batch over money already collected and stamps it released. It does
            NOT refund -- that is Jake's decision, and unit L pins it with a
            test patching `stripe.Refund.create`. So the button never acts on
            one press: it opens a confirmation that names what actually
            happens, including that the refunds are the operator's to make by
            hand.
          -->
          <div class="bm-abandon">
            <button
              v-if="!abandonConfirming"
              type="button"
              class="bm-btn bm-btn--danger"
              :data-testid="`abandon-batch-${batch.id}`"
              :disabled="abandoning"
              @click="onAbandonClick"
            >
              <i class="material-icons bm-btn__icon" aria-hidden="true">block</i>
              Give up on these orders and close the batch
            </button>

            <div
              v-else
              class="bm-abandon__confirm"
              :data-testid="`abandon-confirm-${batch.id}`"
            >
              <b>Close this batch and stop trying?</b>
              <p class="bm-abandon__body">
                <template v-if="chargedStrandedCount > 0">
                  {{ chargedStrandedCount }}
                  {{ chargedStrandedCount === 1 ? 'order' : 'orders' }}
                  already took {{ money(chargedStrandedTotal) }} from
                  {{ chargedStrandedCount === 1 ? 'a shopper' : 'shoppers' }}.
                  Closing the batch does not give that money back:
                  <b>you will need to refund
                  {{ chargedStrandedCount === 1 ? 'them' : 'them' }} by hand</b>,
                  and talk to {{ chargedStrandedCount === 1 ? 'them' : 'them' }}
                  about why. We will list
                  {{ chargedStrandedCount === 1 ? 'it' : 'them' }} here so you
                  do not lose track.
                </template>
                <!--
                  RELEASING, CLOSE FAILED. Each sentence below is pinned to
                  what `ui_abandon_release` actually does on this state:

                  * The close was only ever attempted once no order was left
                    unresolved, so the orders that were PAID FOR reached the
                    decorator. (Not "every order": one declined at charge time
                    was held back and never sent.)
                  * Abandon gives up on any order still waiting to be sent
                    (pending -> abandoned, charged -> held back and flagged)
                    BEFORE it asks for the close, and the endpoint COMMITS
                    whatever the close returns. So if the decorator's system
                    still refuses, the batch stays unreleased BUT those
                    give-ups stand. Rare here, and said plainly.
                  * The refund list is NOT on this dialog (the stranded list is
                    not fetched for this state). The row shows orders owing a
                    refund once the batch is RELEASED (`owesRefunds`), so that
                    is where the copy points.
                  * Stripe is never called; the refund is a human's.
                -->
                <template v-else-if="closeFailed">
                  The orders that were paid for already reached the decorator.
                  Closing asks the decorator's system to accept the batch once
                  more, and gives up for good on any order still waiting to be
                  sent. Closing never gives money back on its own: if an order
                  took money without reaching the decorator, <b>you will need to
                  refund it by hand</b>, and once the batch is released it is
                  listed on this batch as still needing a refund. If the
                  decorator's system still refuses, the batch stays unreleased,
                  but any order you gave up on stays given up.
                </template>
                <template v-else>
                  No money was taken for the orders still outstanding. Closing
                  the batch stops the system retrying them.
                </template>
                <template v-if="closeFailed">
                  Once the decorator's system accepts it, the batch is marked
                  released. This cannot be undone from here.
                </template>
                <template v-else>
                  The batch closes and is marked released. This cannot be undone
                  from here.
                </template>
              </p>
              <div class="bm-abandon__buttons">
                <button
                  type="button"
                  class="bm-btn bm-btn--danger"
                  :data-testid="`abandon-confirm-yes-${batch.id}`"
                  :disabled="abandoning"
                  @click="confirmAbandon"
                >
                  Yes, close it
                </button>
                <button
                  type="button"
                  class="bm-btn"
                  :data-testid="`abandon-confirm-no-${batch.id}`"
                  @click="cancelAbandon"
                >
                  Keep trying
                </button>
              </div>
            </div>
          </div>
        </template>

        <div
          v-if="hasFailedOpen"
          class="bm-failed"
          :data-testid="`open-failed-${batch.id}`"
        >
          <i class="material-icons bm-failed__icon" aria-hidden="true">cloud_off</i>
          <div>
            <!--
              No money claim in either direction beyond the plain fact:
              registration does not charge anyone, and release -- the thing
              that does -- cannot run without an id. Phrased to survive
              #2790's keyword ratchet on affirmative charge verbs.
            -->
            <b>This batch is not registered with the decorator network.</b>
            We tried {{ batch.openAttempts }} times and stopped. It cannot be
            released until it registers.
            <button
              type="button"
              class="bm-btn bm-btn--primary bm-failed__action"
              :data-testid="`retry-open-${batch.id}`"
              @click="$emit('retry-open', batch)"
            >
              <i class="material-icons bm-btn__icon" aria-hidden="true">refresh</i>
              Try registering again
            </button>
          </div>
        </div>

        <div v-if="hasFailedAttempt" class="bm-failed" :data-testid="`failed-${batch.id}`">
          <i class="material-icons bm-failed__icon" aria-hidden="true">error_outline</i>
          <div>
            <b>This batch was not sent. Check each order's payment status before you retry.</b>
            {{ batch.failure }} Fix the problem, then retry.
          </div>
        </div>
      </div>

      <div class="bm-detail__orders">
        <BatchEmptyState
          v-if="batch.orders.length === 0"
          testid="empty-open-batch"
          title="This batch is open and empty"
          :body="`Nothing has been ordered yet. As shoppers check out, their orders land here and the ${rules.windowHours}-hour clock starts with the first one.`"
        >
          <template #action>
            <button type="button" class="bm-btn" data-testid="copy-store-link" @click="$emit('copy-link')">
              <i class="material-icons bm-btn__icon" aria-hidden="true">link</i>
              Copy the store link
            </button>
          </template>
        </BatchEmptyState>

        <template v-else>
          <!--
            Type-down over the orders in THIS batch (Jake, 2026-09-22: "filter
            orders in a batch by type-down search on name, style number, or
            email address").

            Filtering is client-side and that is CORRECT here, not a shortcut:
            the list paginates on BATCHES, not orders -- `_orders_for_batches`
            selects every order of the batches on the page with no LIMIT -- so
            everything this filters over is already loaded. A client-side
            filter is only a lie when the server is holding rows back.

            `type="search"` gives the native clear affordance for free.
          -->
          <div class="bm-orderfilter">
            <i class="material-icons bm-orderfilter__icon" aria-hidden="true">search</i>
            <input
              v-model="orderFilter"
              type="search"
              class="bm-orderfilter__input"
              placeholder="Filter by name, email, or style number"
              aria-label="Filter orders in this batch"
              data-testid="order-filter"
            />
          </div>

          <div v-if="heldOrders.length" data-testid="held-back-section">
            <div class="bm-heldhead">
              <i class="material-icons bm-heldhead__icon" aria-hidden="true">credit_card_off</i>
              {{ heldOrders.length }} {{ heldOrders.length === 1 ? 'order was' : 'orders were' }}
              held back &mdash; not sent
            </div>
            <BatchOrderRow
              v-for="o in heldOrders"
              :key="o.id"
              :order="o"
              :editable="editable"
              :filter="orderFilter"
              :show-decorator="multipleDecorators"
              :movable="canMove"
              @move="$emit('move-order', o)"
              @edit="$emit('edit-order', o)"
              @cancel="$emit('cancel-order', o)"
            />
            <div class="bm-senthead">{{ sentOrders.length }} orders sent</div>
          </div>

          <!--
            `index` gives the row a POSITIONAL testid alongside its
            id-keyed one. The id-keyed testid (`edit-O-1234`) is unusable
            from a ui-runner scenario: selectors are exact `data-testid=`
            matches with no globbing, and a seeded order's display id is
            not knowable when the scenario is authored (BatchSeeder reuses
            whichever recent orders exist). The positional id is stable
            across seeds, so a scenario can open "the first order".
          -->
          <BatchOrderRow
            v-for="(o, index) in sentOrders"
            :key="o.id"
            :order="o"
            :index="index"
            :editable="editable"
            :filter="orderFilter"
            :show-decorator="multipleDecorators"
            :movable="canMove"
            @move="$emit('move-order', o)"
            @edit="$emit('edit-order', o)"
            @cancel="$emit('cancel-order', o)"
          />

          <!--
            SAY that the filter matched nothing. An empty list is
            indistinguishable from a batch that lost its orders, and that is
            the bug report it would generate. Echoing the term back also
            makes a typo obvious without the reader re-reading the field.
          -->
          <div
            v-if="orderFilter && !heldOrders.length && !sentOrders.length"
            class="bm-orderfilter__empty"
            data-testid="order-filter-empty"
          >
            No orders in this batch match &ldquo;{{ orderFilter }}&rdquo;.
          </div>
        </template>
      </div>

      <!--
        The batch footer. It exists ONLY for Holding, Open and Failed. On a
        Released batch there is no footer at all -- not a disabled one.
      -->
      <!--
        The release footer covers BOTH `holding` and `releasing`, and the
        button is DISABLED rather than removed on the latter.

        Disabled, not hidden, for the reason the close control gives one
        branch down: the operator pressed this button a moment ago, and
        removing it under their cursor collapses the footer mid-interaction.
        A disabled control that names its own state is what tells them the
        press landed.

        `releaseInFlight` is SERVER state (`batches.state`), not a component
        flag -- see its computed. That is what makes this survive a refresh.
      -->
      <div
        v-if="(batch.status === 'holding' || releaseInFlight) && !hasFailedAttempt && !closeFailed"
        class="bm-footer"
      >
        <button
          type="button"
          class="bm-btn bm-btn--primary"
          :class="{ 'bm-btn--busy': releaseInFlight }"
          :data-testid="`release-${batch.id}`"
          :disabled="releaseInFlight"
          :aria-busy="String(releaseInFlight)"
          @click="$emit('release', batch)"
        >
          <template v-if="releaseInFlight">
            <span class="bm-btn__spinner" aria-hidden="true" />
            Releasing&hellip;
          </template>
          <template v-else>
            <i class="material-icons bm-btn__icon" aria-hidden="true">send</i>
            Review &amp; release
          </template>
        </button>

        <!--
          The release progress bar and ETA live in the card HEADER now
          (`bm-release-progress`), visible collapsed or expanded. The footer
          keeps only the button; the note is for a batch not yet releasing.
        -->
        <p v-if="!releaseInFlight" class="bm-footer__note">
          Releasing sends {{ sentOrders.length }} {{ sentOrders.length === 1 ? 'order' : 'orders' }}
          to {{ batch.decoratorTarget }}. It cannot be undone.
        </p>
      </div>

      <div v-else-if="batch.status === 'open'" class="bm-footer">
        <!--
          The control is DISABLED while its own close is in flight, not
          removed the way `confirmRelease`'s is. Release lives behind a
          confirmation overlay that can swap its whole footer out; this
          button sits in the row, and removing it would collapse the footer
          under the user's cursor mid-click. Disabled-plus-spinner keeps the
          layout still and says what is happening.

          `closing` is per-batch (see `closingBatchId` in BatchesPanel):
          another open batch on the same panel stays clickable.
        -->
        <button
          type="button"
          class="bm-btn"
          :class="{ 'bm-btn--busy': closing }"
          :data-testid="`close-${batch.id}`"
          :disabled="closing"
          :aria-busy="String(closing)"
          @click="onCloseClick"
        >
          <template v-if="closing">
            <span class="bm-btn__spinner" aria-hidden="true" />
            Closing&hellip;
          </template>
          <template v-else>
            <i class="material-icons bm-btn__icon" aria-hidden="true">lock_clock</i>
            Close to new orders
          </template>
        </button>
        <p class="bm-footer__note">
          Stops collecting and moves this batch to Holding for review. Still nothing charged.
        </p>
      </div>

      <!--
        RESOLVED #2772 (Jake, 2026-08-28): retry is surfaced from HOLDING,
        and there is no `failed` batch state.

        This branch used to be gated on `batch.status === 'failed'`, which
        NOTHING writes -- `batches.state` has exactly two production writers,
        'holding' (batch_lifecycle.py) and 'released' (batch_release.py). So
        the button could never render and the retry was unreachable, even
        though `ui_retry_release` is real and tested from holding
        (`test_retry_works_from_holding`).

        Holding IS the retry state, deliberately: `batch_release` leaves a
        failed close in open/holding precisely so a retry can finish the job.
        The orders are already submitted partner-side and the close is
        idempotent, so marking the batch released would strand them.

        THE GATE IS NOT `status === 'holding'` ALONE. Every batch awaiting
        review is in holding; a retry on all of them would tell a distributor
        something went wrong when nothing did. It is holding AND a RECORDED
        prior failure -- which is why the backend half of #2772 persists that
        failure on the row (`batches.last_release_error`) instead of losing
        it when the page reloads. `hasFailedAttempt` is that conjunction.
      -->
      <div v-else-if="hasFailedAttempt" class="bm-footer">
        <button
          type="button"
          class="bm-btn bm-btn--primary"
          :data-testid="`retry-${batch.id}`"
          @click="$emit('retry', batch)"
        >
          <i class="material-icons bm-btn__icon" aria-hidden="true">refresh</i>
          Retry release
        </button>
        <p class="bm-footer__note">
          Sends the {{ sentOrders.length }}
          {{ sentOrders.length === 1 ? 'order' : 'orders' }} that have not
          reached {{ batch.decoratorTarget }} yet. Anything the decorator already
          accepted is not sent twice.
        </p>
      </div>

      <!--
        RETRY ON A RELEASING BATCH WHOSE CLOSE FAILED (Jake, 2026-10-22).
        Replaces the disabled "Releasing..." button above, which is what this
        batch showed for the 72 h of automatic retries and forever after.
        Same `retry` event and testid as the holding retry: the panel calls
        the same endpoint, which re-tries the CLOSE alone for a releasing
        batch. Shown from the FIRST failure, not only after the give-up: the
        one-hour "Action needed" email says retrying is safe, and the server
        accepts exactly this state (`close_retry` is served from its gate).
      -->
      <div v-else-if="closeFailed" class="bm-footer">
        <button
          type="button"
          class="bm-btn bm-btn--primary"
          :data-testid="`retry-${batch.id}`"
          @click="$emit('retry', batch)"
        >
          <i class="material-icons bm-btn__icon" aria-hidden="true">refresh</i>
          Retry release
        </button>
        <p class="bm-footer__note">
          Asks {{ batch.decoratorTarget }} to accept the batch again, now.
          Orders are not sent twice and no card is charged again.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * One batch: the collapsed row and, when expanded, its detail.
 *
 * The load-bearing rule here is RELEASED IS INERT. Every action affordance --
 * the footer, the per-order Edit/Cancel -- is behind a v-if on the batch
 * state, so a released batch renders no buttons at all. It is deliberately
 * NOT `:disabled="isReleased"`: a disabled button still tells the distributor
 * that an action exists on an immutable batch, and the design says the footer
 * "does not exist on a released batch".
 */
import BatchStatusChip from "./BatchStatusChip.vue";
import BatchOrderRow from "./BatchOrderRow.vue";
import BatchEmptyState from "./BatchEmptyState.vue";
import ChargeAtReleaseNote from "./ChargeAtReleaseNote.vue";
import StrandedOrders from "./StrandedOrders.vue";
import BatchDetails from "./BatchDetails.vue";
import { isEditable, needsYou, statusMeta, chargeFacts, chargedSentence } from "./batchStatus";
import { releaseEta } from "./releaseEta";
import {
  money,
  batchTotal,
  chargedTotal,
  orderMatchesFilter,
  DEFAULT_RULES,
  decoratorLabel,
  decoratorSentLine,
} from "@/services/batchManagementService";

export default {
  name: "BatchRow",
  components: {
    BatchStatusChip,
    BatchOrderRow,
    BatchEmptyState,
    ChargeAtReleaseNote,
    StrandedOrders,
    BatchDetails,
  },
  props: {
    batch: { type: Object, required: true },
    rules: { type: Object, default: () => ({ ...DEFAULT_RULES }) },
    expanded: { type: Boolean, default: false },
    // A close is in flight for THIS batch. Owned by the panel, which owns
    // the request, so the row stays a pure function of its props.
    closing: { type: Boolean, default: false },
    // Live drain progress for THIS batch, polled by the panel. Null until
    // the first poll resolves -- which is a real state, not a missing one:
    // a batch that just flipped to `releasing` has a progress bar with no
    // numbers yet, and rendering 0/0 there would say "nothing has happened"
    // when the truth is "we have not looked yet".
    progress: { type: Object, default: null },
    // An abandon is in flight for THIS batch. Owned by the panel, which owns
    // the request, for the same reason `closing` is.
    abandoning: { type: Boolean, default: false },
    // Release-item ids with a retry in flight, same ownership.
    busyItemIds: { type: Array, default: () => [] },
    // The store has ANOTHER editable batch, so this batch's orders can be
    // moved into it. Owned by the panel, which is the only component that
    // sees every batch; the row only shows or hides the control.
    canMove: { type: Boolean, default: false },
  },
  emits: [
    "toggle",
    "release",
    "close-batch",
    "retry",
    // Distinct from "retry" -- that one re-runs a RELEASE, this one re-runs
    // back-office REGISTRATION. They hit different endpoints and a batch can
    // need both. Sharing one event would send an unregistered batch down the
    // release path, which cannot succeed.
    "retry-open",
    // The per-ITEM retry, distinct from both batch-level retries above: it
    // hits /release-items/<id>/retry and re-arms ONE stranded order.
    "retry-item",
    // The per-ITEM RE-ARM, distinct from "retry-item" above. That one nudges a
    // LIVE item; this one revives a TERMINAL one whose attempts were spent
    // against a cause that has since been repaired (the 2026-09-25 B-31
    // incident). Separate event, separate route, separate audit line.
    "rearm-item",
    // Destructive: closes the batch over charged orders. Emitted only after
    // the confirmation is accepted.
    "abandon",
    "edit-order",
    "cancel-order",
    // Move one (or, in the dialog, several) of this batch's orders to
    // another editable batch of the store. The panel owns the dialog and
    // the call.
    "move-order",
    "copy-link",
    // #475: the server's re-read batch after a name/description save, for
    // the panel's `replaceBatch`.
    "details-updated",
  ],
  data() {
    return {
      // Reactive, unlike `_lastCloseClick`: the confirmation renders from it.
      abandonConfirming: false,
      // The live type-down term. Local to the row, because it is a VIEW
      // concern of one expanded batch -- collapsing and reopening a batch
      // should not inherit a filter the reader set on a different one.
      orderFilter: "",
      // Clock for the ETA, refreshed whenever a poll delivers new progress
      // (see the `progress` watcher) -- no timer of its own.
      etaNow: Date.now(),
    };
  },
  watch: {
    progress() {
      this.etaNow = Date.now();
    },
  },
  computed: {
    editable() {
      return isEditable(this.batch.status);
    },
    /**
     * A release was ATTEMPTED on this batch and did not release it.
     *
     * #2772. Two conditions, and both are load-bearing:
     *
     *   1. `batch.failure` -- a recorded reason from the LAST attempt. The
     *      backend persists it (`batches.last_release_error`) and CLEARS it
     *      on a successful release, so a batch that never attempted, and one
     *      that attempted and succeeded, both read false here. Without this
     *      the gate is "is it in holding", which is every batch awaiting
     *      review -- a retry button on all of them tells a distributor
     *      something went wrong when nothing did.
     *
     *   2. the state is still RETRYABLE. `batch_release` refuses a release
     *      from anything outside open|holding, and `ui_retry_release` returns
     *      409 for the same reason, so offering the button elsewhere would
     *      surface an action the API rejects. A RELEASED batch in particular
     *      must never show it: released is inert by design, and the release
     *      it would retry already succeeded.
     *
     * The reason is checked for TRUTHINESS, not for a specific token. The
     * vocabulary is an allow-list on the server
     * (`services/batch_error_tokens.py`) that grows as the charge seam lands;
     * matching tokens here would mean a new one silently stops surfacing the
     * retry.
     *
     * `status === 'failed'` is ALSO accepted, but only because the DESIGN
     * FIXTURE (`makeFixtureBatches`, B-1044) still uses it and BatchesPanel
     * falls back to that fixture when the endpoint is missing. NO API path
     * emits it -- `batches.state` is written only 'holding' and 'released'
     * -- so this arm is fixture-only and carries no live behaviour. It is
     * kept rather than removed so the fallback screen still explains itself;
     * when the fixture goes, so does this clause.
     */
    /**
     * True when back-office registration gave up for good.
     *
     * Reads the TERMINAL flag alone, deliberately -- not
     * `!backOfficeRegistered`. A batch mid-retry is also unregistered, and
     * telling a distributor it has failed while the system is still working
     * on it produces a support ticket for something that fixes itself in
     * fifteen minutes. Only the exhausted state is worth a human's
     * attention, which is exactly what `open_failed` marks.
     *
     * Not gated on batch state: a registration failure is orthogonal to the
     * release lifecycle and matters in `open` and `holding` alike. It cannot
     * appear on a released batch, because releasing requires the id whose
     * absence this reports.
     */
    hasFailedOpen() {
      return Boolean(this.batch.openFailed);
    },
    /**
     * The drain is RUNNING on this batch, per the SERVER.
     *
     * READS `batch.status`, WHICH IS `batches.state` OFF THE WIRE
     * (`normalizeBatch` maps the column onto the UI's word). That choice is
     * the whole point of this computed and it is not interchangeable with
     * the panel's `releasing` boolean, which is what a reasonable person
     * reaches for first:
     *
     *   - a component flag is set around the POST and reset by ANY remount
     *     -- a page refresh, a route change, a re-render of the list. The
     *     operator refreshes, the button comes back live on a batch the
     *     server is still draining, and they release it a second time.
     *   - `batches.state` survives all of that, because it is not ours.
     *
     * Design philosophy principle 4: the button's disabled state and the
     * batch's actual state are the same fact, so they derive from one
     * source. The panel's flag stays -- it is the right guard for the
     * double-click inside a single mount -- but it does not reach this row.
     */
    /**
     * ETA for the drain. Recomputed when the polled `progress` changes (the
     * panel's existing 3s tick) via `etaNow`, not from a new timer.
     */
    etaInfo() {
      // Before the first poll lands there is no rate to speak of.
      if (!this.progress) {
        return this.releaseInFlight
          ? { kind: "estimating", label: "Estimating..." }
          : { kind: "none", label: "" };
      }
      const p = this.progress;
      return releaseEta({
        startedAt: p.startedAt,
        itemCount: p.itemCount,
        done: p.done,
        resolved: p.resolved,
        state: this.batch.status,
        now: this.etaNow,
      });
    },
    releaseInFlight() {
      return this.batch.status === "releasing";
    },
    /**
     * A RELEASING batch whose back-office close FAILED (#4665).
     *
     * Reads the SERVED `closeRetry`, which the server derives from the same
     * policy function its Retry gate uses -- so Retry and Abandon appear
     * exactly when `ui_retry_release` / `ui_abandon_release` accept them
     * (design philosophy principle 4). Never `failure`: a releasing batch can
     * carry an older episode's token while its drain runs normally.
     *
     * RELEASING ONLY. A failed synchronous close leaves a batch in HOLDING
     * with the same phase, and that batch already has its own Retry
     * (`hasFailedAttempt`, #2772) and no Abandon -- unchanged.
     */
    closeFailed() {
      return this.releaseInFlight && Boolean(this.batch.closeRetry);
    },
    /** The automatic close retries have given up (`close_retry_stopped`). */
    closeRetryStopped() {
      return this.closeFailed && this.batch.closeRetry === "stopped";
    },
    /**
     * Fraction of the batch the drain has finished, 0..1.
     *
     * `resolved`, not `done`, is the numerator. `done` is what shipped;
     * `resolved` is what the drain will not touch again, which includes
     * held-back and abandoned items. A batch that has entirely finished with
     * two cards declined has `done` short of the total forever, and a bar
     * driven by it sits at 78% on a batch nothing will ever move again --
     * which reads as a hang and invites a pointless retry.
     *
     * Guarded against a zero denominator: `release_item_count` is 0 both
     * before the release starts and on an empty batch, and 0/0 must render
     * an empty bar rather than NaN.
     */
    releasePct() {
      const p = this.progress;
      if (!p || !p.itemCount) return 0;
      return Math.min(1, Math.max(0, p.resolved / p.itemCount));
    },
    /**
     * True when the close gate stopped this release (backend unit E).
     *
     * Reads the STATE, not the failure token. `batches.state` is the one
     * authoritative fact here -- the server sets `release_blocked` in the
     * same statement that refuses the close -- whereas `failure` is a token
     * whose vocabulary is an allow-list that grows, and matching on it would
     * mean a future token silently stops rendering the banner. This is the
     * same reasoning `hasFailedAttempt` gives for checking truthiness rather
     * than a specific token, applied one level up.
     *
     * Deliberately independent of `hasFailedAttempt`: a blocked batch carries
     * a `last_release_error` too, so both would otherwise be true, and the
     * template resolves that by testing this one FIRST.
     */
    isReleaseBlocked() {
      return this.batch.status === "release_blocked";
    },
    /** Released, with abandoned orders not yet refunded (option A). */
    owesRefunds() {
      return this.batch.status === "released" && this.batch.refundsOwed > 0;
    },
    /** The orders still owed a refund -- the server's `refund_needed`. */
    owedItems() {
      return this.strandedItems.filter((i) => i.refundNeeded);
    },
    /**
     * The per-item stranded detail, defaulted to empty rather than assumed.
     *
     * An older API build, or a batch whose items have not been fetched yet,
     * serves no `strandedItems`. The panel must still render the blocked
     * banner and the abandon control in that case: the batch IS blocked --
     * `status` says so, and that is the server's own word -- and hiding the
     * only way out because a secondary fetch has not landed would strand the
     * operator along with the orders.
     */
    strandedItems() {
      return this.batch.strandedItems || [];
    },
    /**
     * Stranded items that actually hold money.
     *
     * The `held_back` discriminator again, and the number the confirmation
     * quotes. An item with no charge is one nobody owes anything for, and
     * counting it would overstate the damage in the one place a human is
     * being asked to consent to it.
     */
    chargedStranded() {
      return this.strandedItems.filter((i) => i.chargedCents > 0);
    },
    /**
     * Did a stranded order's card get charged? true / false / null.
     *
     * Read from the stranded items' ledger-backed `chargedCents` (the same
     * fact the panel below prints), NOT from the batch state: `release_blocked`
     * alone covers both "charged, decorator never got it" and "decorator
     * refused it, nothing taken". null while no items are loaded -- the list is
     * fetched on expand and can be empty on a genuinely blocked batch, so
     * absence is "not known", never "uncharged".
     */
    blockedMoneyTaken() {
      if (this.batch.status !== "release_blocked") return null;
      if (!this.strandedItems.length) return null;
      return this.chargedStranded.some((i) => i.chargedCents > 0);
    },
    /**
     * { notSent, total } when part of a blocked batch WAS sent, else null.
     *
     * Batch-level copy ("the batch was not sent", "nothing charged") is only
     * true when NOTHING was sent, and the stranded items cannot say that:
     * they exclude delivered orders. `deliveredOrderIds` (the server's
     * per-order outcome, merged in by the panel) can. Absent = unknown, which
     * keeps the stranded-only wording.
     */
    blockedPartial() {
      if (this.batch.status !== "release_blocked") return null;
      const delivered = this.batch.deliveredOrderIds;
      if (!delivered || !delivered.length) return null;
      const ids = new Set(delivered.map(Number));
      const live = (this.batch.orders || []).filter(
        (o) => o.paymentState !== "canceled"
      );
      const sent = live.filter((o) => ids.has(Number(o.rawId))).length;
      if (!sent) return null;
      const facts = chargeFacts(live, delivered, this.strandedItems);
      return {
        notSent: live.length - sent,
        total: live.length,
        // undefined when not known: the chip then makes no money claim.
        charged: facts.known ? facts.count : undefined,
        chargedSentence: chargedSentence(facts),
      };
    },
    chargedStrandedCount() {
      return this.chargedStranded.length;
    },
    chargedStrandedTotal() {
      return (
        this.chargedStranded.reduce((sum, i) => sum + i.chargedCents, 0) / 100
      );
    },
    hasFailedAttempt() {
      if (!this.batch.failure) return false;
      // HOLDING only, not isEditable(). An OPEN batch is still accruing
      // orders and still has a release countdown to show; treating it as
      // "failed" replaced that countdown with an error banner AND left no
      // retry button, because the open footer arm wins the v-else-if chain
      // before the retry arm is reached. A manual release of an open batch
      // can fail (RELEASABLE_STATES admits "open"), and the failure write is
      // state-unconditional, so this state is reachable in production.
      return this.batch.status === "holding" || this.batch.status === "failed";
    },
    total() {
      return batchTotal(this.batch);
    },
    charged() {
      return chargedTotal(this.batch);
    },
    /**
     * The batch's orders, narrowed by the type-down term.
     *
     * One filtered list feeding both sections, so the held/sent split and
     * the filter cannot disagree about which orders exist. An empty term
     * matches everything (`orderMatchesFilter` returns true), so the
     * unfiltered case costs one predicate call per order and no branch.
     */
    visibleOrders() {
      return (this.batch.orders || []).filter((o) =>
        orderMatchesFilter(o, this.orderFilter)
      );
    },
    heldOrders() {
      return this.visibleOrders.filter((o) => o.held);
    },
    sentOrders() {
      return this.visibleOrders.filter((o) => !o.held);
    },
    decoratorText() {
      return decoratorLabel(this.batch.decoratorRouting);
    },
    // Several decorators: each order row names its own, rather than a list
    // of names in the header that does not say which order went where.
    multipleDecorators() {
      return (this.batch.decoratorRouting || {}).kind === "multiple";
    },
    releasedLine() {
      // Each clause only when its value exists, so a legacy row missing a
      // timestamp reads short rather than "Closed  · Released".
      const b = this.batch;
      return [
        b.closed ? `Closed ${b.closed}` : "",
        b.released ? `Released ${b.released}` : "Released",
        // Worded per routing case; "" (omitted) when unknown, because the
        // Released chip already says it went to the decorator.
        decoratorSentLine(b.decoratorRouting),
      ]
        .filter(Boolean)
        .join(" \u00b7 ");
    },
    thresholdPct() {
      const t = Number(this.rules.threshold) || 0;
      if (!t) return 0;
      return Math.min(100, (this.batch.blanks / t) * 100);
    },
    railClass() {
      // Only a batch that needs you, or one still collecting, carries a rail.
      // Released gets none -- inert means visually quiet too.
      if (needsYou(this.batch.status)) return `bm-card--rail-${statusMeta(this.batch.status).tone}`;
      if (this.batch.status === "open") return "bm-card--rail-blue";
      return "";
    },
  },
  methods: {
    money,

    /**
     * Emit at most one close intent per in-flight close.
     *
     * TWO guards, because they catch different things and the cheap one is
     * not sufficient. The `closing` prop is the real guard, but it only
     * goes true once the parent has re-rendered; a trackpad that fires two
     * `click` events a few milliseconds apart can slip BOTH through that
     * window. The local timestamp closes it synchronously, before any
     * reactivity has to run.
     *
     * 400ms is above a double-click interval and far below the time a
     * human takes to decide to retry a close that looked stuck.
     */
    /**
     * Open the confirmation. DOES NOT ABANDON.
     *
     * The separation is the guard: abandon closes a batch over money that has
     * already left a shopper's account, and the API has no undo. One press
     * must never be able to do that, so this method's entire job is to ask.
     */
    onAbandonClick() {
      if (this.abandoning) return;
      this.abandonConfirming = true;
    },

    cancelAbandon() {
      this.abandonConfirming = false;
    },

    /**
     * Emit the abandon intent, at most once.
     *
     * Closing the confirmation SYNCHRONOUSLY is what makes this idempotent:
     * the `v-if` that renders the confirm button is gone before any
     * reactivity has to run, and a second call finds `abandonConfirming`
     * already false and returns. `BatchRow.onCloseClick` needs a timestamp
     * debounce as well because its button stays mounted across the click;
     * here the affordance is destroyed by the first call, so the flag alone
     * closes the window a double-click opens.
     */
    confirmAbandon() {
      if (!this.abandonConfirming) return;
      this.abandonConfirming = false;
      this.$emit("abandon", this.batch);
    },

    onCloseClick() {
      if (this.closing) return;
      const now = Date.now();
      if (now - this._lastCloseClick < 400) return;
      this._lastCloseClick = now;
      this.$emit("close-batch", this.batch);
    },
  },
  created() {
    // Not reactive: nothing renders from it, and making it reactive would
    // re-render the row on every click.
    this._lastCloseClick = 0;
  },
};
</script>

<style scoped>
.bm-card {
  background: var(--tf-surface);
  border: 1px solid var(--tf-border);
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(19, 55, 82, 0.06);
}

.bm-abandon {
  margin: 12px 0 4px;
}

.bm-abandon__confirm {
  border: 1px solid var(--tf-error);
  border-radius: 10px;
  padding: 12px 14px;
  background: rgba(200, 42, 42, 0.06);
  font-size: 13px;
  line-height: 1.5;
}

.bm-abandon__body {
  margin: 6px 0 10px;
}

.bm-abandon__buttons {
  display: flex;
  gap: 8px;
}

.bm-btn--danger {
  background: var(--tf-error);
  border-color: var(--tf-error);
  color: #fff;
}

.bm-card--rail-blue {
  border-left: 4px solid var(--tf-primary);
}

/* Amber rail: Holding, and only Holding. */
.bm-card--rail-amber {
  border-left: 4px solid var(--tf-warning);
}

.bm-card--rail-red {
  border-left: 4px solid var(--tf-error);
}

.bm-card__header {
  display: grid;
  grid-template-columns: 1.15fr 1.5fr auto;
  gap: 18px;
  align-items: center;
  padding: 15px 20px;
  cursor: pointer;
}

.bm-card__ident {
  min-width: 0;
}

.bm-card__idline {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* A long status chip ("Releasing - Sending to the decorator") is nowrap by
   default and spills out of this narrow column into the trigger column, where
   it collides with the release progress text. Let it wrap inside the column. */
.bm-card__idline :deep(.bm-chip) {
  white-space: normal;
  max-width: 100%;
}

.bm-card__id {
  font-size: 16.5px;
  font-weight: 500;
  color: var(--tf-secondary);
}

/* #475: long names truncate in the header; the details show them whole. */
.bm-card__name {
  font-size: 14.5px;
  color: var(--tf-fg-1);
  max-width: 28ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bm-card__heldpill {
  font-size: 11.5px;
  color: var(--tf-chip-error-fg);
  background: var(--tf-chip-error-bg);
  border: 1px solid var(--tf-chip-error-border);
  border-radius: 9999px;
  padding: 2px 9px;
}

.bm-card__sub {
  font-size: 12.5px;
  color: var(--tf-fg-quiet);
  margin-top: 6px;
}

.bm-card__trigger {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.bm-trigger__clock,
.bm-trigger__holding,
.bm-trigger__released,
.bm-trigger__failed {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  flex-wrap: wrap;
}

.bm-trigger__clock {
  color: var(--tf-fg-2);
}

.bm-trigger__holding {
  color: var(--tf-chip-pending-fg);
}

.bm-trigger__released {
  color: var(--tf-fg-3);
}

.bm-trigger__failed {
  color: var(--tf-error-dark);
  align-items: flex-start;
  line-height: 1.5;
}

.bm-trigger__icon {
  font-size: 16px;
  flex-shrink: 0;
}

.bm-trigger__quiet {
  color: var(--tf-fg-quiet);
}

.bm-trigger__clock b,
.bm-trigger__holding b {
  font-weight: 500;
}

.bm-threshold__labels {
  display: flex;
  justify-content: space-between;
  font-size: 11.5px;
  color: var(--tf-fg-3);
  margin-bottom: 4px;
}

.bm-threshold__target {
  color: var(--tf-fg-quiet);
}

.bm-threshold__track {
  height: 6px;
  background: var(--tf-surface-dark);
  border-radius: 9999px;
  overflow: hidden;
}

.bm-threshold__fill {
  height: 100%;
  background: var(--tf-primary);
  border-radius: 9999px;
}

.bm-threshold__fill--met {
  background: var(--tf-success);
}

.bm-card__chev {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  color: var(--tf-fg-4);
}

.bm-detail {
  background: var(--tf-surface-light);
  border-top: 1px solid var(--tf-border);
}

.bm-detail__top {
  padding: 18px 20px;
}

.bm-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  margin-bottom: 16px;
}

.bm-stat__label {
  font-size: 10.5px;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: var(--tf-fg-quiet);
  font-weight: 500;
}

.bm-stat__value {
  font-size: 17px;
  color: var(--tf-fg-1);
  font-weight: 500;
  margin-top: 3px;
}

.bm-stat__sub {
  font-size: 12px;
  color: var(--tf-fg-quiet);
  margin-top: 2px;
}

.bm-failed {
  display: flex;
  gap: 10px;
  background: var(--tf-error-soft);
  border: 1px solid var(--tf-chip-error-border);
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13px;
  color: var(--tf-chip-error-fg);
  line-height: 1.55;
}

.bm-failed b {
  font-weight: 500;
}

.bm-failed__icon {
  font-size: 18px;
  margin-top: 1px;
  flex-shrink: 0;
}

.bm-detail__orders {
  /* The container BatchOrderRow's narrow-list layout queries (card #604). */
  container: bm-orders / inline-size;
  background: var(--tf-surface);
  border-top: 1px solid var(--tf-border);
}

.bm-orderfilter {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-top: 1px solid var(--tf-border);
}

.bm-orderfilter__icon {
  font-size: 18px;
  color: var(--tf-fg-quiet);
  flex-shrink: 0;
}

.bm-orderfilter__input {
  flex: 1;
  min-width: 0;
  border: 1px solid var(--tf-border-strong);
  border-radius: var(--tf-radius-md, 12px);
  background: var(--tf-surface);
  color: var(--tf-fg-1);
  font-family: inherit;
  /* 16px or iOS Safari zooms the page on focus; 44px for the touch floor. */
  font-size: 16px;
  min-height: 44px;
  padding: 6px 12px;
  outline: none;
}

.bm-orderfilter__input:focus {
  border-color: var(--tf-primary);
  box-shadow: 0 0 0 3px var(--tf-primary-soft);
}

.bm-orderfilter__empty {
  padding: 16px 20px;
  font-size: 13px;
  color: var(--tf-fg-3);
  border-top: 1px solid var(--tf-border);
}

.bm-heldhead {
  padding: 10px 20px;
  background: var(--tf-error-soft);
  border-top: 1px solid var(--tf-chip-error-border);
  font-size: 12.5px;
  color: var(--tf-error-dark);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 7px;
}

.bm-heldhead__icon {
  font-size: 16px;
}

.bm-senthead {
  padding: 10px 20px;
  background: var(--tf-surface-muted);
  border-top: 1px solid var(--tf-border);
  font-size: 12.5px;
  color: var(--tf-fg-3);
  font-weight: 500;
}

.bm-footer {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 14px 20px;
  border-top: 1px solid var(--tf-border);
  background: var(--tf-surface);
  flex-wrap: wrap;
}

.bm-footer__note {
  font-size: 12.5px;
  color: var(--tf-fg-quiet);
  flex: 1;
  margin: 0;
}

/*
  Release progress. Deliberately built from the same pieces as
  `.bm-threshold__track` above -- same height, same radius, same tokens --
  rather than a new bar treatment. Two progress bars on one surface that look
  different are two things to learn; these are the same thing measured against
  different denominators.
*/
.bm-release-progress {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.bm-release-progress__rail {
  height: 6px;
  background: var(--tf-surface-dark);
  border-radius: 9999px;
  overflow: hidden;
}

.bm-release-progress__fill {
  height: 100%;
  background: var(--tf-primary);
  border-radius: 9999px;
  /*
    Eased rather than instant: the bar is repainted on a 3s poll, so a jump
    is the honest shape of the data. The transition is shorter than the poll
    interval so it always settles before the next tick -- an animation still
    running when new data arrives would make the bar look like it was moving
    continuously, which would overstate what we actually know.
  */
  transition: width 300ms ease-out;
}

.bm-release-progress__label {
  font-size: 12.5px;
  color: var(--tf-fg-quiet);
  margin: 0;
}

/* Its own line: an inline "dot + ETA" wraps with the dot orphaned at the
   start of the next line when the label is long. */
.bm-release-progress__eta {
  display: block;
}

.bm-btn {
  border-radius: 9999px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 44px;
  padding: 9px 18px;
  font-size: 13.5px;
  border: 1px solid var(--tf-border-strong);
  background: var(--tf-surface);
  color: var(--tf-fg-2);
}

.bm-btn--primary {
  background: var(--tf-primary);
  border-color: var(--tf-primary);
  color: var(--tf-fg-on-primary);
}

.bm-btn__icon {
  font-size: 16px;
}

.bm-btn:disabled {
  cursor: default;
}

/*
  The busy close control. Dimmed rather than greyed to the point of
  invisibility: it still has to read as the control you just pressed.
*/
.bm-btn--busy {
  opacity: 0.7;
}

/* Same spinner as the release overlay's, sized for an inline button. */
.bm-btn__spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid var(--tf-primary-soft);
  border-top-color: var(--tf-primary);
  display: inline-block;
  animation: bm-spin 800ms linear infinite;
}

@keyframes bm-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Mobile: single column, stacked, full-width primary action. */
@media (max-width: 599px) {
  .bm-card__header {
    display: block;
    padding: 14px 16px;
  }

  .bm-card__trigger {
    margin-top: 12px;
  }

  .bm-card__chev {
    justify-content: flex-end;
    margin-top: 8px;
  }

  .bm-detail__top {
    padding: 16px;
  }

  .bm-stats {
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  .bm-footer {
    padding: 16px;
  }

  .bm-footer .bm-btn {
    width: 100%;
  }

  .bm-footer__note {
    flex: 1 0 100%;
    text-align: center;
  }
}
</style>
